import { useState, useEffect, useRef, useCallback } from 'react';
import { STATE_NAME_TO_ABBR } from '../data/states';

interface LocationState {
  state: string | null;
  isGPS: boolean;
  loading: boolean;
  error: string | null;
  lat: number | null;
  lon: number | null;
}

const GEOCODE_CACHE = new Map<string, string | null>();
const SIGNIFICANT_MOVE_KM = 5;

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
  if (GEOCODE_CACHE.has(key)) return GEOCODE_CACHE.get(key) ?? null;

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=5`,
      {
        headers: {
          'Accept-Language': 'en-US',
          'User-Agent': 'PlateSeeker/1.0 (road-trip-game)',
        },
      }
    );
    const data = await res.json();
    if (data.address?.country_code !== 'us') {
      GEOCODE_CACHE.set(key, null);
      return null;
    }
    const stateName = data.address?.state as string | undefined;
    const abbr = stateName ? (STATE_NAME_TO_ABBR[stateName] ?? null) : null;
    GEOCODE_CACHE.set(key, abbr);
    return abbr;
  } catch {
    return null;
  }
}

export function useLocation() {
  const [location, setLocation] = useState<LocationState>({
    state: null,
    isGPS: false,
    loading: false,
    error: null,
    lat: null,
    lon: null,
  });

  const watchIdRef = useRef<number | null>(null);
  const lastGeocodedRef = useRef<{ lat: number; lon: number } | null>(null);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation(prev => ({
        ...prev,
        loading: false,
        error: 'Geolocation is not supported by this browser.',
      }));
      return;
    }

    setLocation(prev => ({ ...prev, loading: true, error: null }));

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude: lat, longitude: lon } = position.coords;

        const last = lastGeocodedRef.current;
        const moved = !last || distanceKm(last.lat, last.lon, lat, lon) >= SIGNIFICANT_MOVE_KM;

        if (!moved) return;

        lastGeocodedRef.current = { lat, lon };
        const stateAbbr = await reverseGeocode(lat, lon);

        setLocation({
          state: stateAbbr,
          isGPS: true,
          loading: false,
          error: stateAbbr ? null : 'Could not determine US state from your location.',
          lat,
          lon,
        });
      },
      (err) => {
        const msgs: Record<number, string> = {
          1: 'Location permission denied. Please select your state manually.',
          2: 'Location unavailable. Please select your state manually.',
          3: 'Location request timed out. Please select your state manually.',
        };
        setLocation({
          state: null,
          isGPS: false,
          loading: false,
          error: msgs[err.code] ?? 'Location error.',
          lat: null,
          lon: null,
        });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, []);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const setManualState = useCallback((abbr: string) => {
    stopWatching();
    setLocation({ state: abbr, isGPS: false, loading: false, error: null, lat: null, lon: null });
  }, [stopWatching]);

  useEffect(() => () => stopWatching(), [stopWatching]);

  return { location, startWatching, stopWatching, setManualState };
}
